import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  bookingCancelReasonLabels,
  bookingCancelReasons,
  toBerlinDateKey,
  type BookingCancelReason,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import { BookingFrame } from './BookingFrame';
import { BookingLoading, BookingMessage, RaceBanner } from './BookingPage';
import { Fact, OfferPanel, type Offer } from './OfferPanel';
import { SlotChooser } from './SlotChooser';
import {
  cancelBooking,
  createHold,
  icsUrl,
  isGone,
  isNotFound,
  manageViewKey,
  reschedule,
  slotTakenOf,
  useManageView,
  usePublicPage,
  type BookingTime,
  type ManageView,
  type SlotTaken,
} from './api';
import { channelShort, isVideo } from './format';
import { readStored, removeStored, writeStored } from './storage';
import styles from './Booking.module.css';

const TWO_HOUR_RULE = 'Umbuchen ist nur bis 2 Stunden vor dem Termin möglich.';

type View = 'overview' | 'reschedule' | 'cancel';

function CheckBadge() {
  return (
    <span className={styles.check} aria-hidden="true">
      <span className={styles.checkMark} />
    </span>
  );
}

/** „…, 30 Minuten per Video. Die Bestätigung mit dem Link zum Gespräch ist unterwegs an <E-Mail>." (05) */
function confirmationText(view: ManageView): string {
  const { appointment } = view;
  if (!appointment.email) return appointment.text;
  const sentence = isVideo(appointment.channelLine)
    ? `Die Bestätigung mit dem Link zum Gespräch ist unterwegs an ${appointment.email}.`
    : `Die Bestätigung ist unterwegs an ${appointment.email}.`;
  return `${appointment.text} ${sentence}`;
}

/** Buchungsseite 05 (desktop) and the second phone frame of 09. */
function Confirmed({
  token,
  view,
  mobile,
  onReschedule,
  onCancel,
}: {
  token: string;
  view: ManageView;
  mobile: boolean;
  onReschedule: () => void;
  onCancel: () => void;
}) {
  const { appointment } = view;
  const past = view.state === 'past';
  if (mobile) {
    const [day, time] = splitHeading(appointment.shortLabel);
    return (
      <div className={styles.mobileStack}>
        <div className={styles.resultCardMobile}>
          <div className={styles.eyebrowRow}>
            <CheckBadge />
            <span className={styles.eyebrow}>Bestätigt</span>
          </div>
          <h1 className={styles.mobileResultTitle}>
            {day}
            <br />
            {time} Uhr
          </h1>
          <p className={styles.mobileResultText}>
            {appointment.eventTypeName} mit {appointment.advisor.name}, {appointment.durationMinutes} Minuten
            {isVideo(appointment.channelLine)
              ? ` per ${channelShort(appointment.channelLine)}. Den Link finden Sie in Ihrer E-Mail.`
              : '.'}
          </p>
          {!past ? (
            <div className={styles.mobileActions}>
              <a className={styles.stickyPrimary} href={icsUrl(token)} download="termin.ics">
                In Kalender eintragen
              </a>
              <button type="button" className={styles.stickySecondarySmall} onClick={onReschedule}>
                Umbuchen
              </button>
              {view.canCancel ? (
                <span className={styles.centerLink}>
                  <button type="button" className={styles.quietLink} onClick={onCancel}>
                    Termin absagen
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className={styles.narrow}>
      <div className={styles.resultCard}>
        <div className={styles.eyebrowRow}>
          <CheckBadge />
          <span className={styles.eyebrow}>Termin bestätigt</span>
        </div>
        <h1 className={styles.resultTitleLarge}>{appointment.heading}</h1>
        <p className={styles.resultText}>{confirmationText(view)}</p>
        {appointment.reminderText ? (
          <div className={styles.resultFacts}>
            <Fact label="Erinnerung" value={appointment.reminderText} />
          </div>
        ) : null}
        {!past ? (
          <>
            <div className={styles.resultActions}>
              <a className={styles.primaryButton} href={icsUrl(token)} download="termin.ics">
                In Kalender eintragen
              </a>
              <button type="button" className={styles.secondaryButton} onClick={onReschedule}>
                Umbuchen
              </button>
            </div>
            <div className={styles.resultFooter}>
              Passt der Termin nicht? Buchen Sie einfach um.
              {view.canCancel ? (
                <button type="button" className={styles.quietLink} onClick={onCancel}>
                  Termin absagen
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** „Do, 8. Oktober · 16:00" → [„Do, 8. Oktober", „16:00"]. */
function splitHeading(shortLabel: string): [string, string] {
  const index = shortLabel.lastIndexOf(' · ');
  return index === -1 ? [shortLabel, ''] : [shortLabel.slice(0, index), shortLabel.slice(index + 3)];
}

/** Buchungsseite 06 — new time for the same link; the old one is released when the new one is confirmed. */
function Reschedule({
  token,
  view,
  mobile,
  onDone,
}: {
  token: string;
  view: ManageView;
  mobile: boolean;
  onDone: () => Promise<void>;
}) {
  const { orgSlug, typeSlug } = view.booking;
  const page = usePublicPage(orgSlug, typeSlug);
  const storageScope = `t/${token}`;
  const [race, setRace] = useState<SlotTaken | null>(null);
  const [notice, setNotice] = useState<string | null>(view.canReschedule ? null : TWO_HOUR_RULE);
  const [busy, setBusy] = useState(false);

  async function confirm(time: BookingTime) {
    setBusy(true);
    setNotice(null);
    try {
      const previous = readStored<{ holdKey: string }>(storageScope, 'hold');
      const hold = await createHold(orgSlug, typeSlug, time.startsAt, previous?.holdKey ?? null);
      writeStored(storageScope, 'hold', hold);
      await reschedule(token, hold.holdKey);
      removeStored(storageScope, 'hold');
      setRace(null);
      await onDone();
    } catch (error) {
      const taken = slotTakenOf(error);
      if (taken) setRace(taken);
      else setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const { appointment } = view;
  const offer: Offer = {
    organizationName: view.organization.name,
    advisor: page.data?.advisor ?? appointment.advisor,
    eventTypeName: appointment.eventTypeName,
    description: page.data?.eventType.description ?? null,
    durationMinutes: appointment.durationMinutes,
    durationLabel: page.data?.eventType.durationLabel ?? `${appointment.durationMinutes} Minuten`,
    channelLine: appointment.channelLine,
  };
  const previous = (
    <div className={styles.previous}>
      <div className={styles.previousLabel}>Bisher</div>
      <div className={styles.previousValue}>{appointment.shortLabel}</div>
    </div>
  );
  const banner = (
    <>
      {race ? <RaceBanner race={race} mobile={mobile} /> : null}
      {notice ? (
        <Banner tone="warning" className={styles.notice}>
          {notice}
        </Banner>
      ) : null}
    </>
  );
  const chooser = page.data ? (
    <SlotChooser
      orgSlug={orgSlug}
      typeSlug={typeSlug}
      earliest={page.data.earliest}
      latest={page.data.latest}
      mobile={mobile}
      initialDate={toBerlinDateKey(appointment.startsAt)}
      race={race}
      banner={banner}
      footnote="Umbuchen ist bis 2 Stunden vor dem Termin möglich. Der alte Termin wird freigegeben, sobald Sie den neuen bestätigen."
      busy={busy || !view.canReschedule}
      mobileLabel={(time) => `Auf ${time.label} umbuchen`}
      mobileNote="Der alte Termin wird dann freigegeben."
      mobileHeading="Neuen Termin wählen"
      onContinue={(time) => void confirm(time)}
    />
  ) : page.isError ? (
    <Banner tone="danger">{errorMessage(page.error)}</Banner>
  ) : null;

  if (mobile) {
    return (
      <div className={styles.mobileStack}>
        {previous}
        {chooser}
      </div>
    );
  }
  return (
    <div className={styles.card}>
      <div className={styles.cardLeft}>
        <OfferPanel
          offer={offer}
          before={
            <>
              <div className={styles.eyebrow}>Termin umbuchen</div>
              {previous}
            </>
          }
        />
      </div>
      <div className={styles.cardRight}>{chooser}</div>
    </div>
  );
}

/** Buchungsseite 07 — „Lieber einen anderen Termin?": rebooking first, cancelling as the understated link. */
function Cancel({
  token,
  view,
  mobile,
  onReschedule,
  onDone,
}: {
  token: string;
  view: ManageView;
  mobile: boolean;
  onReschedule: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState<BookingCancelReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { appointment } = view;

  async function cancel() {
    setBusy(true);
    setNotice(null);
    try {
      await cancelBooking(token, reason);
      await onDone();
    } catch (error) {
      setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const chips = (
    <div className={styles.chips} role="group" aria-label="Grund">
      {bookingCancelReasons.map((value) => (
        <button
          key={value}
          type="button"
          className={clsx(styles.chip, reason === value && styles.chipSelected)}
          aria-pressed={reason === value}
          onClick={() => setReason((current) => (current === value ? null : value))}
        >
          {bookingCancelReasonLabels[value]}
        </button>
      ))}
    </div>
  );
  const noticeBanner = notice ? (
    <Banner tone="danger" className={styles.notice}>
      {notice}
    </Banner>
  ) : null;

  if (mobile) {
    return (
      <div className={styles.mobileStack}>
        <h1 className={styles.mobileCancelTitle}>Lieber einen anderen Termin?</h1>
        <p className={styles.mobileResultText}>
          Sie können den Termin am {appointment.inlineLabel}, in wenigen Sekunden auf einen anderen Tag legen.
        </p>
        <div className={styles.mobileDivider} />
        <div className={styles.reasonLabel}>Falls Sie absagen: Grund (optional)</div>
        {chips}
        {noticeBanner}
        <div className={styles.sticky}>
          <button type="button" className={styles.stickyPrimary} onClick={onReschedule}>
            Anderen Termin wählen
          </button>
          <span className={styles.centerLink}>
            <button
              type="button"
              className={styles.quietLink}
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => void cancel()}
            >
              Trotzdem absagen
            </button>
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.narrow}>
      <div className={styles.resultCard}>
        <div className={styles.eyebrow}>Termin absagen</div>
        <h1 className={styles.resultTitle}>Lieber einen anderen Termin?</h1>
        <p className={styles.resultText}>
          Sie können den Termin am {appointment.inlineLabel}, in wenigen Sekunden auf einen anderen Tag legen.{' '}
          {appointment.advisor.name} erfährt es sofort.
        </p>
        <div className={styles.reasonLabelDesktop}>
          Falls Sie absagen: Möchten Sie einen Grund nennen? (optional)
        </div>
        {chips}
        {noticeBanner}
        <div className={styles.cancelActions}>
          <button type="button" className={styles.primaryButtonLarge} onClick={onReschedule}>
            Anderen Termin wählen
          </button>
          <button
            type="button"
            className={styles.quietLink}
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void cancel()}
          >
            Trotzdem absagen
          </button>
        </div>
      </div>
    </div>
  );
}

/** Buchungsseite 08 — the customer's side of a cancellation (the internal follow-up is the host's task). */
function Cancelled({ view, mobile }: { view: ManageView; mobile: boolean }) {
  const href = `/book/${view.booking.orgSlug}/${view.booking.typeSlug}`;
  return (
    <div className={mobile ? styles.mobileStack : styles.narrow}>
      <div className={mobile ? styles.resultCardMobile : styles.resultCard}>
        <div className={styles.eyebrow}>Abgesagt</div>
        <h1 className={mobile ? styles.mobileCancelledTitle : styles.resultTitle}>
          Ihr Termin ist abgesagt.
        </h1>
        <p className={mobile ? styles.mobileResultText : styles.resultText}>
          {mobile
            ? 'Sie erhalten eine Bestätigung per E-Mail. Falls Sie es sich anders überlegen, buchen Sie einfach neu.'
            : 'Sie erhalten eine Bestätigung per E-Mail. Falls Sie es sich anders überlegen, buchen Sie einfach einen neuen Termin.'}
        </p>
        <div className={styles.resultActions}>
          <Link to={href} className={mobile ? styles.stickySecondary : styles.secondaryButtonLarge}>
            Neuen Termin buchen
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Buchungsseite 05–08 (+ 09/10 at 390): `/book/t/:token` — the customer's link from the confirmation. */
export function ManagePage() {
  const { token = '' } = useParams();
  const mobile = useIsMobile();
  const queryClient = useQueryClient();
  const query = useManageView(token);
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view: View = requested === 'reschedule' || requested === 'cancel' ? requested : 'overview';
  // Each step is a history entry, so the browser's back button returns to the previous one.
  const setView = (next: View, replace = false) =>
    setParams(next === 'overview' ? {} : { view: next }, { replace });

  if (query.isPending) return <BookingLoading />;
  if (query.isError) {
    return (
      <BookingFrame>
        {isGone(query.error) ? (
          <BookingMessage
            eyebrow="Link abgelaufen"
            title={errorMessage(query.error)}
            text="Der Termin liegt in der Vergangenheit. Für einen neuen Termin nutzen Sie bitte die Buchungsseite."
          />
        ) : isNotFound(query.error) ? (
          <BookingMessage
            eyebrow="Link nicht gefunden"
            title="Der Termin wurde nicht gefunden."
            text="Bitte prüfen Sie den Link aus Ihrer Bestätigungs-E-Mail."
          />
        ) : (
          <BookingMessage eyebrow="Fehler" title={errorMessage(query.error)} />
        )}
      </BookingFrame>
    );
  }

  const data = query.data;
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: manageViewKey(token) });
    await query.refetch();
    setView('overview', true);
    window.scrollTo(0, 0);
  };

  let content;
  if (data.state === 'cancelled') {
    content = <Cancelled view={data} mobile={mobile} />;
  } else if (view === 'reschedule' && data.state === 'booked') {
    content = <Reschedule token={token} view={data} mobile={mobile} onDone={refresh} />;
  } else if (view === 'cancel' && data.state === 'booked') {
    content = (
      <Cancel
        token={token}
        view={data}
        mobile={mobile}
        onReschedule={() => setView('reschedule')}
        onDone={refresh}
      />
    );
  } else {
    content = (
      <Confirmed
        token={token}
        view={data}
        mobile={mobile}
        onReschedule={() => setView('reschedule')}
        onCancel={() => setView('cancel')}
      />
    );
  }

  return (
    <BookingFrame organization={data.organization} indexable={data.indexable}>
      {content}
    </BookingFrame>
  );
}
